import { Company } from '../../models/company';
import { Service } from '../../models/service';
import { User } from '../../models/user';

/** Decides whether the card is saved to the user or to a company account. */
export enum CardHolder {
  Company = 'company',
  Recipient = 'recipient',
}

/** A recipient can be asked for a card they are never charged against. */
export enum CardReason {
  CompanyPays = 'company_pays',
  RecipientCharge = 'recipient_charge',
  RecipientFees = 'recipient_fees',
  RecipientChargeAndFees = 'recipient_charge_and_fees',
}

export interface CardRequirement {
  holder: CardHolder;
  reason: CardReason;
}

export class PaymentPlan {
  constructor(
    private readonly services: Service[] = [],
    private readonly user?: Nullable<User>,
    private readonly company?: Nullable<Company>,
    private readonly canAddCompanyPaymentMethod = false,
  ) {}

  private get billsRecipient(): boolean {
    return this.services.some((service) => service.billsRecipient);
  }

  private get recipientLiableForFees(): boolean {
    return this.services.some((service) => service.recipientLiableForFees);
  }

  get lateCancellationFee(): number {
    return this.feeLiableServices.reduce((total, service) => total + service.lateCancellationFee, 0);
  }

  get noShowFee(): number {
    return this.feeLiableServices.reduce((total, service) => total + service.noShowFee, 0);
  }

  private get feeLiableServices(): Service[] {
    return this.services.filter((service) => service.recipientLiableForFees);
  }

  private get billsOwner(): boolean {
    return this.services.some((service) => !service.billsRecipient);
  }

  /** With no company the user owns the vehicle, so owner-billed charges them. */
  private get userIsCharged(): boolean {
    return this.billsRecipient || (!this.company && this.billsOwner);
  }

  private get requiresUserCard(): boolean {
    return this.userIsCharged || this.recipientLiableForFees;
  }

  get missingCards(): CardRequirement[] {
    const requirements: CardRequirement[] = [];

    if (this.requiresUserCard && !this.user?.hasValidPaymentMethod) {
      requirements.push({ holder: CardHolder.Recipient, reason: this.recipientReason });
    }

    // Without permission the order is placed and cancelled later instead.
    if (
      this.company &&
      this.billsOwner &&
      !this.company.hasValidPaymentMethod &&
      this.canAddCompanyPaymentMethod
    ) {
      requirements.push({ holder: CardHolder.Company, reason: CardReason.CompanyPays });
    }

    return requirements;
  }

  private get recipientReason(): CardReason {
    if (this.userIsCharged && this.recipientLiableForFees) {
      return CardReason.RecipientChargeAndFees;
    }
    return this.userIsCharged ? CardReason.RecipientCharge : CardReason.RecipientFees;
  }
}
